import { formatInTimeZone } from "date-fns-tz";
import { Device, Facility, Target } from "../../models";

export const PVR_EXPORT_ORG_ID = "f3ee7f35-da27-4c7e-bcac-bd60e7da2d4c";

export const isPvrExportEnabled = (organizationId?: string): boolean =>
    !!organizationId && organizationId.toLowerCase() === PVR_EXPORT_ORG_ID.toLowerCase();

const CARDINALS = ["North", "East", "South", "West"] as const;
const MIN_TRACK_METERS = 0.5;

// The reader that consumes this file matches this line byte-for-byte, spaces included.
const PVR_COLUMN_HEADER = "CollectionTime, Direction, Lane, Class, Speed, videoFileNum, FrameNum, ImageFileName";

export const bearingToCardinal = (bearing: number): string => {
    const normalized = ((bearing % 360) + 360) % 360;
    return CARDINALS[Math.round(normalized / 90) % 4];
};

export const directionOf = (target: Target): string => {
    const start = target.startPosition;
    const end = target.endPosition;
    if (!start || !end) return "";
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    if (Math.hypot(dx, dy) < MIN_TRACK_METERS) return "";
    return bearingToCardinal((Math.atan2(dx, -dy) * 180) / Math.PI);
};

export const laneNumberOf = (lanes: number | undefined): string => {
    const mask = Number(lanes ?? 0);
    if (!mask) return "";
    const lowestBit = mask & -mask;
    return String(Math.log2(lowestBit) + 1);
};

export const speedMphOf = (averageVelocityMps: number | undefined): number =>
    averageVelocityMps == null ? 0 : Math.max(0, Math.round(averageVelocityMps * 2.237));

export const formatSpeed = (averageVelocityMps: number | undefined): string => String(speedMphOf(averageVelocityMps)).padStart(3, "0");

export interface PvrHeaderIdentity {
    stationNumber: string;
    site: string;
    location: string;
    unitSerialNumber: string;
}

export const PVR_REQUIRED_HEADER_FIELDS: Array<{ key: keyof PvrHeaderIdentity; label: string }> = [
    { key: "stationNumber", label: "Station Number" },
    { key: "site", label: "Site" },
    { key: "location", label: "Location" },
    { key: "unitSerialNumber", label: "Unit Serial Number" },
];

export const missingPvrHeaderFields = (identity: PvrHeaderIdentity) => PVR_REQUIRED_HEADER_FIELDS.filter(({ key }) => !identity[key]?.trim());

const firstNonBlank = (...values: Array<string | undefined>): string => values.find((v) => !!v?.trim())?.trim() ?? "";

export const derivePvrHeaderIdentity = (facility?: Facility, devices?: Device[], ms2LocationId?: string): PvrHeaderIdentity => {
    const address = [facility?.address1, facility?.city, facility?.state].map((part) => part?.trim()).filter((part): part is string => !!part);
    const serials = (devices ?? []).map((device) => device.serialNumber?.trim()).filter((serial): serial is string => !!serial);

    return {
        stationNumber: firstNonBlank(ms2LocationId, facility?.locationCode),
        site: firstNonBlank(facility?.name),
        location: address.join(", "),
        unitSerialNumber: serials.join(" / "),
    };
};

export const pvrCoordinates = (facility?: Facility): { latitude: number; longitude: number } => {
    const origin = facility?.originReferencePointLatLng?.origin ?? facility?.originReferencePointLatLng?.points?.[0];
    return { latitude: origin?.y ?? 0, longitude: origin?.x ?? 0 };
};

interface PvrRow {
    collectionTime: string;
    direction: string;
    lane: string;
    fhwaClass: string;
    speedMph: number;
}

export interface PvrExportOptions {
    zone: string;
    reportStart?: Date;
    fileName: string;
    identity: PvrHeaderIdentity;
    latitude?: number;
    longitude?: number;
}

export interface PvrCsvResult {
    csv: string;
    exportedCount: number;
    skippedCount: number;
}

const escapeField = (value: string): string => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

const dominantDirections = (rows: PvrRow[]): [string, string] => {
    const counts = new Map<string, number>();
    for (const row of rows) {
        if (!row.direction) continue;
        counts.set(row.direction, (counts.get(row.direction) ?? 0) + 1);
    }
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return [ranked[0]?.[0] ?? "", ranked[1]?.[0] ?? ""];
};

const isCompleteRecord =(row: PvrRow, baseDirection: string, secondDirection: string): boolean =>
    !!row.direction && (row.direction === baseDirection || row.direction === secondDirection) && !!row.lane && !!row.fhwaClass && row.speedMph > 0;

const distinctLaneCount = (rows: PvrRow[], direction: string): number => new Set(rows.filter((row) => !!direction && row.direction === direction).map((row) => row.lane)).size;

const formatCoordinate = (value: number | undefined): string => (value == null || !Number.isFinite(value) || value === 0 ? "0" : String(Number(value.toFixed(6))));

export const buildPvrCsv = (targets: Target[], opts: PvrExportOptions): PvrCsvResult => {
    const candidates: PvrRow[] = targets
        .filter((t) => t.lastUpdate)
        .map((t) => ({
            collectionTime: formatInTimeZone(t.lastUpdate!, opts.zone, "yyyy-MM-dd HH:mm:ss"),
            direction: directionOf(t),
            lane: laneNumberOf(t.lanes as number | undefined),
            fhwaClass: t.fhwaClass ? String(t.fhwaClass) : "",
            speedMph: speedMphOf(t.averageVelocity),
        }));

    const [baseDirection, secondDirection] = dominantDirections(candidates);
    const rows = candidates.filter((row) => isCompleteRecord(row, baseDirection, secondDirection));

    const times = targets.map((t) => t.lastUpdate?.getTime()).filter((time): time is number => !!time);
    const reportStart = opts.reportStart ?? (times.length ? new Date(Math.min(...times)) : undefined);

    const header = [
        ["Data File:", opts.fileName],
        ["Station Number:", opts.identity.stationNumber],
        ["Site:", opts.identity.site],
        ["Location:", opts.identity.location],
        ["Report Start Time:", reportStart ? formatInTimeZone(reportStart, opts.zone, "yyyy/MM/dd  HH:mm:ss") : ""],
        ["Base Direction:", baseDirection],
        ["latitude:", formatCoordinate(opts.latitude)],
        ["longitude:", formatCoordinate(opts.longitude)],
        ["Lane number Base direction:", String(distinctLaneCount(rows, baseDirection))],
        ["Lane number second direction:", String(distinctLaneCount(rows, secondDirection))],
        ["Classification Type:", " 13 Class Classification"],
        ["Unit Serial Number:", opts.identity.unitSerialNumber],
        [""],
    ];

    const lines = [
        ...header.map((line) => line.map(escapeField).join(",")),
        PVR_COLUMN_HEADER,
        ...rows.map((row) => [row.collectionTime, row.direction, row.lane, row.fhwaClass, String(row.speedMph).padStart(3, "0"), "", "", ""].join(",")),
    ];

    return { csv: lines.join("\n"), exportedCount: rows.length, skippedCount: candidates.length - rows.length };
};
